import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { getIndicatorClassification, IndicatorEsfEnum } from "@/utils/indicatorsEsf";
import { ResumeIndicatorC3ByHealthTeamData } from "@/services/esus/indicator/esf/c3/getResumeIndicatorC3ByHealthTeam";

import { IndicatorEsfC3HealthTeamsFilters } from "./IndicatorEsfC3HealthTeamsFilters";
import { IndicatorEsfC3HealthTeamsClassificationTable } from "./IndicatorEsfC3HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC3HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorC3ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorC3ByHealthTeam: () => Promise<ResumeIndicatorC3ByHealthTeamData[]>;
};

export function IndicatorEsfC3HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorC3ByHealthTeam,
}: Readonly<IndicatorEsfC3HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);
	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorC3ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformHealthTeamsClassificationToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Atendimentos por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformHealthTeamsClassificationToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorC3ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe do atendimento": healthTeamClassification.healthTeam?.name ?? "",
					"Gestantes e puérperas": healthTeamClassification.totalPregnantsAndPuerperals,
					"Pontuação - gestantes e puérperas": healthTeamClassification.totalPregnantsAndPuerperalsScore,
					"Puérperas": healthTeamClassification.totalPuerperals,
					"Pontuação - puérperas": healthTeamClassification.totalPuerperalsScore,
					"% Classificação - puérperas": `${calculatePercentage(
						healthTeamClassification.totalPuerperalsScore,
						healthTeamClassification.maxPuerperalsScore
					)}%`,
					"Classificação - puérperas": getIndicatorClassification(
						IndicatorEsfEnum.C3,
						calculatePercentage(
							healthTeamClassification.totalPuerperalsScore,
							healthTeamClassification.maxPuerperalsScore
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC3HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC3HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
